<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()
const isOpen = ref(false)
const searchQuery = ref('')

const activeContext = computed(() => authStore.activeContext)
const merchantRoles = computed(() => authStore.merchantRoles || [])

const filteredRoles = computed(() => {
  const query = searchQuery.value.toLowerCase().trim()
  if (!query) return merchantRoles.value
  return merchantRoles.value.filter(role => 
    role.merchantName.toLowerCase().includes(query) ||
    role.role.toLowerCase().includes(query)
  )
})

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectMerchant = async (merchantId: string) => {
  try {
    await authStore.setActiveContext(merchantId)
    isOpen.value = false
    searchQuery.value = ''

    // Redirect based on role
    const role = merchantRoles.value.find(r => r.merchantId === merchantId)
    if (role?.role === 'MERCHANT_ADMIN') {
      router.push('/membership/merchant/dashboard')
    } else {
      router.push('/membership/merchant/branch')
    }
  } catch (error) {
    console.error('Failed to switch context', error)
  }
}
</script>

<template>
  <div v-if="merchantRoles.length > 0" class="relative inline-block text-left z-50">
    <div>
      <button
        type="button"
        @click="toggleDropdown"
        class="inline-flex justify-between items-center w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition duration-150 ease-in-out"
        id="menu-button"
        aria-expanded="true"
        aria-haspopup="true"
      >
        <span class="flex items-center space-x-2">
          <span class="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
          <span v-if="activeContext" class="font-semibold text-gray-200">
            {{ activeContext.activeMerchant.name }}
            <span class="text-xs text-purple-400 font-normal">({{ activeContext.activeRole }})</span>
          </span>
          <span v-else class="text-gray-400">
            {{ $t('membership.select_context') || 'Select Merchant' }}
          </span>
        </span>
        <svg class="-mr-1 ml-2 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <!-- Dropdown menu -->
    <div
      v-if="isOpen"
      class="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-lg bg-gray-900 border border-gray-800 shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none backdrop-blur-md bg-opacity-95"
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="menu-button"
      tabindex="-1"
    >
      <div class="p-2 border-b border-gray-800">
        <input
          v-model="searchQuery"
          type="text"
          class="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          :placeholder="$t('membership.search_merchant') || 'Search merchant...'"
        />
      </div>
      <div class="py-1 max-h-60 overflow-y-auto" role="none">
        <button
          v-for="role in filteredRoles"
          :key="role.merchantId"
          @click="selectMerchant(role.merchantId)"
          class="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-800 hover:text-white flex flex-col transition duration-100 ease-in-out"
          :class="{ 'bg-purple-900 bg-opacity-30 border-l-2 border-purple-500 text-white': activeContext?.activeMerchant.id === role.merchantId }"
          role="menuitem"
        >
          <span class="font-medium">{{ role.merchantName }}</span>
          <span class="text-xs text-gray-500">{{ role.role }}</span>
        </button>
        <div v-if="filteredRoles.length === 0" class="px-4 py-3 text-xs text-gray-500 text-center">
          {{ $t('membership.no_merchants_found') || 'No merchants found' }}
        </div>
      </div>
    </div>
  </div>
</template>
